import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass } from "lucide-react";
import LandingNav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

function PageShell({ label, children, testId }) {
  return (
    <main data-testid={testId} className="min-h-screen bg-obsidian text-ink">
      <LandingNav />
      <section className="relative overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="pointer-events-none absolute inset-0 hud-grid radial-fade opacity-30" />
        <div className="pointer-events-none absolute inset-x-0 top-1/3 h-[380px] bg-[radial-gradient(ellipse_at_center,rgba(46,119,136,0.14),transparent_70%)]" />
        <div className="relative mx-auto max-w-4xl px-6 md:px-10">
          <motion.p
            {...fadeUp}
            className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal"
          >
            {label}
          </motion.p>
          {children}
        </div>
      </section>
      <Footer />
    </main>
  );
}

export function About() {
  return (
    <PageShell label="Who's behind the tower" testId="about-page">
      <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-6 font-display text-5xl leading-[1.02] tracking-tight text-ink md:text-6xl">
        A quiet team
        <br />
        <span className="italic text-mist">building calm software.</span>
      </motion.h1>

      <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }} className="mt-10 max-w-2xl text-lg leading-relaxed text-mist">
        Life OS is built by a small group of clinicians, engineers, and parents
        who spent too many years watching modern productivity tools optimize for
        the wrong variable. Households don't need more optimization. They need
        awareness — and permission to slow down.
      </motion.p>

      <div className="mt-16 grid gap-8 border-t border-rule pt-12 md:grid-cols-2">
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.24 }}>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">Origin</div>
          <p className="mt-4 text-base leading-relaxed text-ink">
            Life OS began at the intersection of preventive medicine and
            software craft. We watched people who had every tool — calendars,
            trackers, wearables — still miss the compression that pushed them
            over the edge. The tool was the wrong shape.
          </p>
        </motion.div>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.32 }}>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">Home base</div>
          <p className="mt-4 text-base leading-relaxed text-ink">
            Precipice Health · a small US-based studio. We ship slowly, on
            purpose. We use our own software. If it doesn't make our own week
            calmer, it doesn't go in.
          </p>
        </motion.div>
      </div>

      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.4 }} className="mt-16 border border-rule bg-deep/40 p-8">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
          <Compass className="h-3.5 w-3.5" strokeWidth={1.5} />
          What we're not
        </div>
        <ul className="mt-4 space-y-3 text-base leading-relaxed text-mist">
          <li className="border-l border-teal/30 pl-4">Not a productivity app. Nothing in Life OS makes you "do more".</li>
          <li className="border-l border-teal/30 pl-4">Not an AI coach. We measure. You decide.</li>
          <li className="border-l border-teal/30 pl-4">Not a family surveillance tool. Members opt-in; nothing is broadcast.</li>
          <li className="border-l border-teal/30 pl-4">Not free-forever. This is a real business — invitations first, sustainable pricing after.</li>
        </ul>
      </motion.div>

      <div className="mt-16 flex flex-wrap items-center gap-4">
        <Link
          to="/manifesto"
          data-testid="about-manifesto-cta"
          className="group inline-flex items-center gap-2 border border-rule px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:border-teal"
        >
          Read the manifesto
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.5} />
        </Link>
        <Link
          to="/#waitlist"
          data-testid="about-waitlist-cta"
          className="inline-flex items-center gap-2 bg-teal px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover"
        >
          Request access
        </Link>
      </div>
    </PageShell>
  );
}

export function Manifesto() {
  return (
    <PageShell label="Founding manifesto · v1" testId="manifesto-page">
      <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-6 font-display text-5xl leading-[1.02] tracking-tight text-ink md:text-6xl">
        Households
        <br />
        <span className="italic text-mist">are systems.</span>
      </motion.h1>

      <div className="mt-14 space-y-10 text-lg leading-[1.75] text-mist">
        {[
          {
            heading: "01 · Capacity is a hard limit",
            body:
              "You cannot borrow tomorrow's hours today. Every household has a finite ceiling — sustainable output above which everything else quietly breaks. Most software pretends this ceiling doesn't exist. We don't.",
          },
          {
            heading: "02 · Compression is the enemy",
            body:
              "It's not the number of things on the calendar. It's the density. A day with five obligations stacked without transitions is not the same as a day with five obligations spaced by breath. Life OS names compression because unnamed compression becomes burnout.",
          },
          {
            heading: "03 · Recovery is fuel, not indulgence",
            body:
              "Airlines defend fuel reserves as a matter of law. Households treat recovery as optional. That's the wrong direction. Recovery windows are structural, not aspirational. They are the reason capacity exists next week.",
          },
          {
            heading: "04 · Silence is a feature",
            body:
              "The best software is quiet software. Life OS should interrupt you exactly when a threshold is crossed and never otherwise. No streaks, no achievements, no push notifications for their own sake.",
          },
          {
            heading: "05 · Awareness precedes action",
            body:
              "We do not tell you what to do. We tell you what you are carrying. The decisions belong to you and the humans in your household. Software that removes agency also removes trust.",
          },
          {
            heading: "06 · Households are the unit, not individuals",
            body:
              "A single person's calendar is not a life. Life happens in the collision of many calendars — partners, kids, parents, caregivers. Life OS is designed for that collision, and refuses to reduce it to a single-user view.",
          },
        ].map((s, i) => (
          <motion.div
            key={s.heading}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
            className="border-l border-teal/50 pl-6"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
              {s.heading}
            </div>
            <p className="mt-3 text-ink">{s.body}</p>
          </motion.div>
        ))}
      </div>

      <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.5 }} className="mt-16 border-t border-rule pt-10 font-display text-2xl italic text-mist">
        This is what we build for. If it resonates, join the waitlist.
      </motion.p>
      <div className="mt-6 flex flex-wrap gap-4">
        <Link
          to="/#waitlist"
          data-testid="manifesto-waitlist-cta"
          className="group inline-flex items-center gap-2 bg-teal px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover"
        >
          Request access
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.5} />
        </Link>
        <Link
          to="/contact"
          data-testid="manifesto-contact-cta"
          className="inline-flex items-center gap-2 border border-rule px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:border-amber"
        >
          Contact the team
        </Link>
      </div>
    </PageShell>
  );
}

export function Contact() {
  return (
    <PageShell label="Direct line to the tower" testId="contact-page">
      <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-6 font-display text-5xl leading-[1.02] tracking-tight text-ink md:text-6xl">
        Say hello
        <br />
        <span className="italic text-mist">or tell us where we're wrong.</span>
      </motion.h1>

      <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }} className="mt-10 max-w-2xl text-lg leading-relaxed text-mist">
        The best product feedback comes from households that actually use Life
        OS. If you're on the waitlist, invited, or considering either — write
        us. We read everything.
      </motion.p>

      <div className="mt-14 grid gap-6 md:grid-cols-2">
        <motion.a
          href="mailto:LifeOS@precipice-health.com?subject=Life%20OS%20—%20hello"
          data-testid="contact-email-card"
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.24 }}
          className="group relative block border border-rule bg-deep/40 p-8 transition-colors duration-300 hover:border-teal"
        >
          <span className="absolute -top-2 -left-2 h-4 w-4 border-t border-l border-teal/70" />
          <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-teal/70" />
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-teal">Email</div>
          <div className="mt-4 font-display text-2xl text-ink">LifeOS@precipice-health.com</div>
          <p className="mt-3 text-sm leading-relaxed text-mist">
            Product feedback, invite inquiries, or press. Response window ~2 business days.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-teal transition-transform duration-300 group-hover:translate-x-0.5">
            Compose message <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
          </div>
        </motion.a>

        <motion.a
          href="/#waitlist"
          data-testid="contact-waitlist-card"
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.32 }}
          className="group relative block border border-rule bg-deep/40 p-8 transition-colors duration-300 hover:border-amber"
        >
          <span className="absolute -top-2 -left-2 h-4 w-4 border-t border-l border-amber/70" />
          <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-amber/70" />
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber">Access</div>
          <div className="mt-4 font-display text-2xl text-ink">Request an invitation</div>
          <p className="mt-3 text-sm leading-relaxed text-mist">
            We invite households in small cohorts. Adding your email is the
            fastest way to reach the tower.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-amber transition-transform duration-300 group-hover:translate-x-0.5">
            Join waitlist <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
          </div>
        </motion.a>
      </div>

      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.4 }} className="mt-16 border-t border-rule pt-8 text-sm text-mist">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">Mailing</div>
        <p className="mt-2 text-ink">Precipice Health · United States</p>
        <p className="mt-4">© {new Date().getFullYear()} Life OS · A Precipice Health product.</p>
      </motion.div>
    </PageShell>
  );
}
